import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  LifeBuoy, Plus, Trash2, Check, AlertTriangle, Pencil,
} from "lucide-react";

function EditDialog({ boatId, initialEntries, suggestedSizes, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(() =>
    initialEntries.length ? [...initialEntries] : [{ size: "", quantity: 0 }],
  );

  useEffect(() => {
    if (open) {
      setRows(
        initialEntries.length ? [...initialEntries] : [{ size: "", quantity: 0 }],
      );
    }
  }, [open, initialEntries]);

  const setRow = (i, patch) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((rs) => [...rs, { size: "", quantity: 0 }]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = rows
      .map((r) => ({
        size: (r.size || "").trim(),
        quantity: parseInt(r.quantity, 10) || 0,
      }))
      .filter((r) => r.size && r.quantity > 0);
    try {
      await api.put(`/boats/${boatId}/life-vests`, { entries: clean });
      toast.success("Flytevester oppdatert");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="lifevest-edit-open"
          className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10"
        >
          <Pencil className="w-4 h-4 mr-1.5" /> Rediger
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Rediger flytevester</DialogTitle>
          <DialogDescription>
            Før opp hvilke flytevester som ligger i båten. Antallet og størrelsen
            sjekkes ved hvert renhold.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_120px_40px] gap-2 text-[11px] uppercase tracking-[0.18em] text-[#A57F39]">
            <div>Størrelse</div>
            <div>Antall</div>
            <div></div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
              <Input
                value={r.size}
                onChange={(e) => setRow(i, { size: e.target.value })}
                list="lifevest-sizes"
                placeholder="F.eks. XL eller Barn"
                data-testid={`lifevest-row-${i}-size`}
              />
              <Input
                type="number"
                min={0}
                max={99}
                value={r.quantity}
                onChange={(e) => setRow(i, { quantity: e.target.value })}
                data-testid={`lifevest-row-${i}-qty`}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeRow(i)}
                className="h-9 px-2 border-red-200 text-red-600 hover:bg-red-50"
                data-testid={`lifevest-row-${i}-remove`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <datalist id="lifevest-sizes">
            {suggestedSizes.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <Button
            variant="ghost"
            onClick={addRow}
            className="text-[#1DA3B3]"
            data-testid="lifevest-add-row"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Legg til størrelse
          </Button>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            data-testid="lifevest-save"
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            Lagre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerifyDialog({ boatId, expectedTotal, kind, onDone }) {
  // kind: 'match' | 'discrepancy'
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [actualTotal, setActualTotal] = useState("");
  const isMatch = kind === "match";

  const submit = async () => {
    try {
      const r = await api.post(`/boats/${boatId}/life-vests/verify`, {
        match: isMatch,
        note: note || null,
        actual_total: isMatch ? null : (parseInt(actualTotal, 10) || null),
      });
      if (r.data.defect_created) {
        toast.success("Avvik registrert og sendt til service");
      } else {
        toast.success(isMatch ? "Flytevester bekreftet" : "Avvik registrert");
      }
      setOpen(false);
      setNote("");
      setActualTotal("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke registrere");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isMatch ? (
          <Button
            data-testid="lifevest-verify-match"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={expectedTotal === 0}
          >
            <Check className="w-4 h-4 mr-1.5" /> Stemmer
          </Button>
        ) : (
          <Button
            data-testid="lifevest-verify-discrepancy"
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            disabled={expectedTotal === 0}
          >
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Avvik
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isMatch ? "Bekreft flytevester" : "Registrer avvik"}
          </DialogTitle>
          <DialogDescription>
            {isMatch
              ? `Bekreft at antall og størrelser i båten stemmer med listen (totalt ${expectedTotal} stk).`
              : "Beskriv hva som mangler eller er endret. Et avvik opprettes automatisk til service/admin."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isMatch && (
            <div>
              <Label>Faktisk totalt i båten (valgfritt)</Label>
              <Input
                type="number"
                min={0}
                max={99}
                value={actualTotal}
                onChange={(e) => setActualTotal(e.target.value)}
                placeholder={`Forventet: ${expectedTotal}`}
                data-testid="lifevest-actual-total"
              />
            </div>
          )}
          <div>
            <Label>{isMatch ? "Notat (valgfritt)" : "Beskrivelse av avvik"}</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={
                isMatch
                  ? "F.eks. «Alle vester sjekket og i god stand»"
                  : "F.eks. «Mangler 1 XL. Funnet 1 ekstra Barn-vest.»"
              }
              data-testid="lifevest-verify-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            data-testid="lifevest-verify-submit"
            className={
              isMatch
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"
            }
          >
            {isMatch ? "Bekreft" : "Registrer avvik"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BoatLifeVests({ boatId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);

  const load = async () => {
    const r = await api.get(`/boats/${boatId}/life-vests`);
    setData(r.data);
  };

  useEffect(() => {
    load();
  }, [boatId]);

  if (!data) {
    return <Card className="p-6 text-sm text-[#2B3F4D]">Laster flytevester…</Card>;
  }

  const lastVerifiedTxt = data.last_verified_at
    ? `Sist kontrollert ${new Date(data.last_verified_at).toLocaleString("nb-NO")}${
        data.last_verified_by_name ? ` · ${data.last_verified_by_name}` : ""
      }`
    : "Ikke kontrollert ennå";

  return (
    <div className="space-y-4" data-testid="lifevests-section">
      <Card className="p-5 border-l-4 border-l-[#1DA3B3]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1DA3B3]/15 flex items-center justify-center flex-shrink-0">
              <LifeBuoy className="w-5 h-5 text-[#1DA3B3]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                Totalt i båten
              </div>
              <div
                className="text-3xl font-bold text-[#0C1824]"
                data-testid="lifevest-total"
              >
                {data.total} <span className="text-base font-normal text-[#2B3F4D]">stk</span>
              </div>
              <div className="text-xs text-[#2B3F4D] mt-1">{lastVerifiedTxt}</div>
              {data.last_verified_match === false && (
                <Badge className="bg-red-100 text-red-700 mt-2" data-testid="lifevest-last-discrepancy">
                  Forrige kontroll: avvik registrert
                </Badge>
              )}
              {data.last_verified_match === true && (
                <Badge className="bg-emerald-100 text-emerald-700 mt-2" data-testid="lifevest-last-ok">
                  Forrige kontroll: OK
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <EditDialog
                boatId={boatId}
                initialEntries={data.entries}
                suggestedSizes={data.suggested_sizes || []}
                onSaved={load}
              />
            )}
            <VerifyDialog
              boatId={boatId}
              expectedTotal={data.total}
              kind="match"
              onDone={load}
            />
            <VerifyDialog
              boatId={boatId}
              expectedTotal={data.total}
              kind="discrepancy"
              onDone={load}
            />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 text-xs uppercase tracking-[0.22em] bg-[#0C1824] text-white">
          Oversikt etter størrelse
          <span className="ml-2 font-mono text-[#ECC974]">{data.entries.length}</span>
        </div>
        {data.entries.length === 0 ? (
          <div className="p-6 text-sm text-[#2B3F4D]">
            Ingen flytevester registrert ennå.
            {isAdmin && " Bruk «Rediger» for å føre opp hvilke som ligger i båten."}
          </div>
        ) : (
          <div>
            {data.entries.map((e, i) => (
              <div
                key={`${e.size}-${i}`}
                data-testid={`lifevest-entry-${i}`}
                className="grid grid-cols-[1fr_auto] gap-3 items-center py-3 px-4 border-b border-slate-100 last:border-0"
              >
                <div className="font-medium text-[#0C1824]">{e.size}</div>
                <div className="text-sm font-mono text-[#A57F39]">×{e.quantity}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.last_verified_note && (
        <Card className="p-4 bg-slate-50 border-l-4 border-l-slate-400">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-600">
            Notat fra siste kontroll
          </div>
          <div className="text-sm text-[#2B3F4D] mt-1 italic">«{data.last_verified_note}»</div>
        </Card>
      )}
    </div>
  );
}
